import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flote/ui';
import type {
  AdminPartitionFulfillmentStatus,
  AdminPartitionFulfillmentType,
} from '@flote/contracts/http';
import { deliveryTsr } from '../../api';
import { PartitionStatusBadge, PartitionTypeBadge } from '../../components/status-badges';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StatusCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const ALL_VALUE = '__ALL__';

export function PartitionsPage() {
  const navigate = useNavigate();
  const [fulfillmentType, setFulfillmentType] = useState<string>(ALL_VALUE);
  const [fulfillmentStatus, setFulfillmentStatus] = useState<string>(ALL_VALUE);
  const [showFloteManaged, setShowFloteManaged] = useState<boolean>(false);

  // Build query params
  const queryParams: {
    limit: number;
    fulfillmentType?: AdminPartitionFulfillmentType;
    fulfillmentStatus?: AdminPartitionFulfillmentStatus;
    floteManaged?: boolean;
  } = { limit: 50 };

  if (fulfillmentType !== ALL_VALUE) {
    queryParams.fulfillmentType = fulfillmentType as AdminPartitionFulfillmentType;
  }
  if (fulfillmentStatus !== ALL_VALUE) {
    queryParams.fulfillmentStatus = fulfillmentStatus as AdminPartitionFulfillmentStatus;
  }
  if (showFloteManaged) {
    queryParams.floteManaged = true;
  }

  // Fetch partitions
  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = deliveryTsr.adminPartitions.list.useQuery({
    queryKey: ['partitions-list', fulfillmentType, fulfillmentStatus, showFloteManaged],
    queryData: { query: queryParams },
  });

  const partitions = response?.status === 200 ? response.body.items : [];
  const totalCount = response?.status === 200 ? (response.body.totalCount ?? partitions.length) : 0;

  // Calculate status counts from fetched data
  const statusCounts = partitions.reduce(
    (acc, p) => {
      if (p.fulfillmentStatus === 'pending_offer' || p.fulfillmentStatus === 'offer_sent') {
        acc.pending++;
      } else if (p.fulfillmentStatus === 'in_progress' || p.fulfillmentStatus === 'assigned') {
        acc.inProgress++;
      } else if (p.fulfillmentStatus === 'completed') {
        acc.completed++;
      } else if (p.fulfillmentStatus === 'declined' || p.fulfillmentStatus === 'expired' || p.fulfillmentStatus === 'cancelled') {
        acc.issues++;
      }
      return acc;
    },
    { pending: 0, inProgress: 0, completed: 0, issues: 0 }
  );

  const handleClearFilters = () => {
    setFulfillmentType(ALL_VALUE);
    setFulfillmentStatus(ALL_VALUE);
    setShowFloteManaged(false);
  };

  const hasFilters = fulfillmentType !== ALL_VALUE || fulfillmentStatus !== ALL_VALUE || showFloteManaged;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Partitions</h1>
          <p className="text-muted-foreground">
            Manage order partitions and fulfillment assignments
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-full sm:w-auto"
        >
          {isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard
          title="Pending"
          count={statusCounts.pending}
          icon={Clock}
          color="bg-amber-100 text-amber-600"
          isLoading={isLoading}
        />
        <StatusCard
          title="In Progress"
          count={statusCounts.inProgress}
          icon={Truck}
          color="bg-blue-100 text-blue-600"
          isLoading={isLoading}
        />
        <StatusCard
          title="Completed"
          count={statusCounts.completed}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          isLoading={isLoading}
        />
        <StatusCard
          title="Issues"
          count={statusCounts.issues}
          icon={AlertCircle}
          color="bg-red-100 text-red-600"
          isLoading={isLoading}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-full sm:w-48">
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                Fulfillment Type
              </label>
              <Select value={fulfillmentType} onValueChange={setFulfillmentType}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>All types</SelectItem>
                  <SelectItem value="flote_driver">Flote Driver</SelectItem>
                  <SelectItem value="brand_driver">Brand Driver</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-48">
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                Status
              </label>
              <Select value={fulfillmentStatus} onValueChange={setFulfillmentStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
                  <SelectItem value="pending_offer">Pending Offer</SelectItem>
                  <SelectItem value="offer_sent">Offer Sent</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={showFloteManaged ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFloteManaged(!showFloteManaged)}
              >
                <Truck className="mr-2 h-4 w-4" />
                Flote Managed Only
              </Button>
            </div>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Partitions List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Partitions {totalCount > 0 && <span className="text-muted-foreground font-normal">({totalCount})</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : partitions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No partitions found</h3>
              <p className="mb-4 max-w-sm text-sm text-muted-foreground">
                {hasFilters
                  ? 'No partitions match your current filters. Try adjusting your filters.'
                  : 'Partitions will appear here when orders are partitioned for fulfillment.'}
              </p>
              {hasFilters && (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tracking ID</TableHead>
                      <TableHead className="hidden sm:table-cell">Brand</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden md:table-cell">Total</TableHead>
                      <TableHead className="hidden lg:table-cell">Payment</TableHead>
                      <TableHead className="hidden lg:table-cell">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partitions.map((partition) => (
                      <TableRow
                        key={partition.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/partitions/${partition.id}`)}
                      >
                        <TableCell className="font-mono text-sm">{partition.trackingId}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {partition.brandName || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <PartitionTypeBadge type={partition.fulfillmentType} />
                        </TableCell>
                        <TableCell>
                          <PartitionStatusBadge status={partition.fulfillmentStatus} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {formatCurrency(partition.portionTotal)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {partition.paymentMethod || '—'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {formatDate(partition.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
